import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import React, { useState, useEffect } from "react";
import {
    useGetInnovationPodByStudentIdQuery,
    useGetUserByIdQuery,
    useGetUsersQuery,
    useLazyGetCourseProgressQuery,
    useGetInnovationPodByIdQuery,
} from "@/APIConnect";
import { Skeleton } from "@/components/ui/skeleton";

import { PodLayout } from "./components/PodLayout";
import IntendedOutcomesModal from "./components/IntendedOutcomesModal";
import ProblemStatementModal from "./components/ProblemStatementModal";

const extractId = (id: any): string => {
    if (!id) return '';
    if (typeof id === 'string') return id;
    if (typeof id === 'object') {
        const { timestamp, machine, pid, increment } = id;
        if (timestamp !== undefined && machine !== undefined && pid !== undefined && increment !== undefined) {
            const h = (n: number, len: number) => (n >>> 0).toString(16).padStart(len, '0').slice(-len);
            return `${h(timestamp, 8)}${h(machine, 6)}${h(pid, 4)}${h(increment, 6)}`.toLowerCase();
        }
    }
    return String(id);
};

export default function InnovationPodDashboard() {
    const navigate = useNavigate();
    const { user } = useAuth0();
    const userId = user?.sub?.replace('auth0|', '') || '';

    const { data: usersResponse } = useGetUsersQuery({});
    const allUsers = usersResponse?.resultObject || usersResponse || [];

    const [isOutcomesModalOpen, setIsOutcomesModalOpen] = useState(false);
    const [isProblemModalOpen, setIsProblemModalOpen] = useState(false);

    // Course Selection State from URL
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedCourseId = searchParams.get('courseId');
    const selectedClassroomId = searchParams.get('classroomId');
    const [innovationPodId, setInnovationPodId] = useState<string | null>(null);


    // Lazy query for course progress
    const [getCourseProgress, { isFetching: progressLoading }] = useLazyGetCourseProgressQuery();

    // Fetch Pod Data by ID (or fallback to studentId if nothing selected yet)
    const { data: idResponse, isLoading: idLoading, error: idError } = useGetInnovationPodByIdQuery(innovationPodId, { skip: !innovationPodId });
    const { data: studentResponse, isLoading: studentLoading, error: studentError } = useGetInnovationPodByStudentIdQuery(userId, { skip: !userId || !!innovationPodId || !!selectedCourseId });

    // When a course is selected, only use the pod from that course's progress (or null if none)
    const rawPodData = selectedCourseId
        ? (innovationPodId ? idResponse?.resultObject : null)
        : (innovationPodId ? idResponse?.resultObject : studentResponse?.resultObject);
    const loading = progressLoading || idLoading || (!selectedCourseId && studentLoading);
    const error = selectedCourseId ? (innovationPodId ? idError : null) : (innovationPodId ? idError : studentError);

    // Normalize all stage IDs to strings so comparisons work correctly
    const podData = React.useMemo(() => {
        if (!rawPodData) return rawPodData;
        return {
            ...rawPodData,
            podStages: rawPodData.podStages?.map((stage: any) => ({
                ...stage,
                id: extractId(stage.id),
            })),
        };
    }, [rawPodData]);


    // Effect to fetch progress when URL params change
    useEffect(() => {
        const fetchProgress = async () => {
            if (selectedCourseId && selectedClassroomId) {
                try {
                    const progress = await getCourseProgress({ courseId: selectedCourseId, classroomId: selectedClassroomId }).unwrap();
                    const result = progress?.resultObject || progress;
                    setInnovationPodId(extractId(result?.innovationPodId) || null);
                } catch (err) {
                    console.error("Failed to fetch progress:", err);
                    setInnovationPodId(null);
                }
            }
        };
        fetchProgress();
    }, [selectedCourseId, selectedClassroomId]);



    // Show skeleton while loading OR while we have no data yet (prevents flash of empty state)
    if (loading && !podData) {
        return (
            <PodLayout>
                {/* Only render content skeletons - PodLayout already handles hero + tabs */}
                <div className="pcard anim a2">
                    <div className="pcard-h">
                        <Skeleton className="h-4 w-32" />
                    </div>
                    <div className="j-track" style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
                        <Skeleton className="h-16 w-24 rounded" />
                        <Skeleton className="h-16 w-24 rounded" />
                        <Skeleton className="h-16 w-24 rounded" />
                    </div>
                </div>

                {/* Main Grid Skeleton */}
                <div className="pgrid">
                    <div className="space-y-4">
                        {/* Problem Statement + Team Split */}
                        <div className="psplit anim a3">
                            <div className="pcard" style={{ margin: 0, flex: 1 }}>
                                <div className="pcard-h">
                                    <Skeleton className="h-4 w-32" />
                                </div>
                                <Skeleton className="h-24 w-full mt-4 rounded" />
                            </div>

                            <div className="pcard" style={{ margin: 0, flex: 1 }}>
                                <div className="pcard-h">
                                    <Skeleton className="h-4 w-20" />
                                </div>
                                <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                                    <Skeleton className="h-12 w-12 rounded-full" />
                                    <Skeleton className="h-12 w-12 rounded-full" />
                                    <Skeleton className="h-12 w-12 rounded-full" />
                                </div>
                            </div>
                        </div>

                        {/* Gates & Proofs Skeleton */}
                        <div className="pcard anim a4">
                            <div className="pcard-h" style={{ justifyContent: 'space-between' }}>
                                <Skeleton className="h-4 w-28" />
                                <Skeleton className="h-3 w-16" />
                            </div>
                            <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <Skeleton className="h-12 w-full rounded" />
                                <Skeleton className="h-12 w-full rounded" />
                            </div>
                        </div>
                    </div>

                    {/* Right Sidebar Skeleton */}
                    <div className="pright space-y-4">
                        <div className="pcard">
                            <div className="pcard-h">
                                <Skeleton className="h-4 w-16" />
                            </div>
                            <Skeleton className="h-16 w-full mt-3 rounded" />
                        </div>

                        <div className="pcard">
                            <div className="pcard-h">
                                <Skeleton className="h-4 w-16" />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
                                <Skeleton className="h-10 w-10 rounded-full" />
                                <div style={{ flex: 1 }}>
                                    <Skeleton className="h-3 w-20 mb-1" />
                                    <Skeleton className="h-2 w-16" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </PodLayout>
        );
    }
    // Only show empty state when we're certain there's no data (loading complete and error or no data)
    if (!podData && !loading) return <PodLayout isEmpty><div /></PodLayout>;

    const getName = (id: string) => {
        const targetId = extractId(id);
        const member = podData?.podMembership?.students?.find((s: any) => extractId(s.studentId) === targetId);
        if (member) return member.studentName;

        if (targetId === extractId(podData?.podMembership?.instructorId)) return podData?.podMembership?.instructorName || 'Instructor';
        if (targetId === extractId(podData?.podMembership?.mentorId)) return podData?.podMembership?.mentorName || 'Mentor';

        const u = allUsers.find((user: any) => extractId(user.id) === targetId);
        return u?.displayName || u?.fullName || (u?.firstName && u?.lastName ? `${u.firstName} ${u.lastName}` : u?.name) || id;
    };

    const members = podData?.podMembership?.studentIds?.map((id: string) => ({
        id: extractId(id),
        name: getName(id),
        role: 'Student'
    })) || [];

    const activeStageIndex = podData?.podStages?.findIndex((st: any) => !st.isCompleted) ?? 0;

    return (
        <PodLayout podData={podData} activeTab="overview">
            <div className="pcard anim a2">
                <div className="pcard-h">
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M2 7h10M9 4l3 3-3 3" />
                    </svg>Innovation Journey
                </div>
                <div className="j-track">
                    <div className="j-line"><div className="j-fill" style={{ width: `${Math.max(10, ((activeStageIndex) / (podData?.podStages?.length || 1)) * 100)}%` }}></div></div>

                    {podData?.podStages?.map((stage: any, index: number) => {
                        const isDone = stage.isCompleted;
                        const isCurrent = !isDone && podData?.podStages?.findIndex((s: any) => !s.isCompleted) === index;
                        const stateClass = isDone ? 'done' : (isCurrent ? 'on' : '');
                        return (
                            <div key={stage.id} className={`j-step ${stateClass}`}>
                                <div className="j-num">{index + 1}</div>
                                <div className="j-icon">
                                    {index === 0 && <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="8" r="3" /><path d="M8 2v2M8 12v2M2 8h2M12 8h2" /></svg>}
                                    {index === 1 && <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="8" r="5" /><path d="M6 8h4M8 6v4" /></svg>}
                                    {index === 2 && <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="10" height="10" rx="2" /><path d="M6 8l2 2 3-3" /></svg>}
                                    {index === 3 && <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 12V6l4-3 4 3v6" /><path d="M7 12V9h2v3" /></svg>}
                                    {index >= 4 && <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M8 2l2 4h4l-3.5 3 1.5 5L8 11l-4 3 1.5-5L2 6h4z" /></svg>}
                                </div>
                                <div className="j-nm">{stage.stageName}</div>
                                <div className="j-desc truncate">{stage.stageDescription}</div>
                                {isCurrent && <div className="j-badge">Active · Stage {index + 1}</div>}
                            </div>
                        )
                    })}
                </div>
            </div>

            <div className="pgrid">
                <div>
                    <div className="psplit anim a3">
                        <div className="pcard" style={{ margin: 0 }}>
                            <div className="pcard-h">
                                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="7" cy="7" r="5.5" /><path d="M7 4.5v3l2 1.5" /></svg>
                                Pod Description
                            </div>
                            <div className="prob-text line-clamp-3">{podData?.problemStatement || 'No problem statement defined.'}</div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '6px' }}>
                                <button className="prob-view-full" style={{ margin: 0 }} onClick={() => setIsProblemModalOpen(true)}>
                                    View full
                                </button>
                                <div className="pod-tag" style={{ margin: 0 }}><svg width="8" height="8" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M1 4l2.5 2.5L7 2" /></svg>Outcomes defined</div>
                            </div>
                            <button className="prob-more" style={{ marginTop: '12px' }} onClick={() => setIsOutcomesModalOpen(true)}>
                                <svg width="8" height="8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="4" cy="4" r="3" /><path d="M4 2.5v1.5M4 5v.5" /></svg>
                                View Details
                            </button>
                        </div>

                        <div className="pcard" style={{ margin: 0 }}>
                            <div className="pcard-h">
                                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="5" cy="5" r="2.5" /><path d="M1 11c0-2 1.8-3.5 4-3.5s4 1.5 4 3.5" /></svg>
                                Team · {members.length}
                            </div>
                            <div className="t-grid">
                                {members.map((member: any, i: number) => {
                                    const gradients = [
                                        'linear-gradient(135deg,var(--peach),var(--rose))',
                                        'linear-gradient(135deg,var(--sky),var(--violet))',
                                        'linear-gradient(135deg,var(--teal),var(--brand))'
                                    ];
                                    return (
                                        <div className="t-mem" key={member.id}>
                                            <div className="t-av" style={{ background: gradients[i % gradients.length] }}>
                                                {member.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                                                <div className="t-on"></div>
                                            </div>
                                            <div className="t-nm">{member.name.split(' ')[0]}</div>
                                            <div className="t-rl">{member.role}</div>
                                        </div>
                                    )
                                })}
                                <div className="t-mem">
                                    <div className="t-add"><svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M6 2v8M2 6h8" /></svg></div>
                                    <div className="t-nm" style={{ color: 'var(--text-4)' }}>Invite</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="pcard anim a4">
                        <div className="pcard-h" style={{ justifyContent: 'space-between' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path d="M3 2h8a1 1 0 011 1v10l-5-3-5 3V3a1 1 0 011-1z" />
                                </svg>
                                Gates & Proofs
                            </span>
                            <span style={{ fontSize: '9px', fontWeight: 600, color: 'var(--text-4)' }}>{podData?.podStages?.length || 0} GATES</span>
                        </div>

                        <div className="ev-timeline">
                            {podData?.podStages?.map((stage: any, index: number) => {
                                const isDone = stage.isCompleted;
                                const isCurrent = !isDone && podData?.podStages?.findIndex((s: any) => !s.isCompleted) === index;
                                const colorVar = isDone ? 'var(--teal)' : (isCurrent ? 'var(--peach)' : 'var(--border)');
                                const textColor = isDone ? 'var(--teal)' : (isCurrent ? 'var(--peach)' : 'var(--text-4)');

                                return (
                                    <div className={`ev-step ${isDone ? 'done' : ''} ${isCurrent ? 'current' : ''}`} key={stage.id}>
                                        <div className="ev-step-dot" style={{ background: colorVar, color: isDone ? '#fff' : (isCurrent ? '#fff' : 'var(--text-4)') }}>
                                            {isDone ? '✓' : index + 1}
                                        </div>
                                        <div className="ev-step-head">
                                            <div className="ev-step-title" style={{ color: textColor }}>Step {index + 1}: {stage.stageName}</div>
                                            <div className="ev-step-sub">{stage.stageDescription}</div>
                                        </div>
                                        <div className="ev-proofs">
                                            {isDone ? (
                                                <div className="ev-proof" style={{ background: 'var(--teal-soft)', borderColor: 'rgba(13,191,138,.12)' }}>
                                                    <div className="ev-proof-bar" style={{ background: 'var(--teal)' }}></div>
                                                    <div className="ev-proof-info">
                                                        <div className="ev-proof-nm" style={{ color: 'var(--teal-deep)' }}>Gate {index + 1}</div>
                                                        <div className="ev-proof-tags"><span className="ev-proof-tag">Completed</span></div>
                                                    </div>
                                                    <div className="ev-proof-status" style={{ background: 'var(--teal)' }}>✓ Validated</div>
                                                </div>
                                            ) : isCurrent ? (
                                                <div className="ev-proof" style={{ boxShadow: '0 1px 4px rgba(0,0,0,.04)' }}>
                                                    <div className="ev-proof-bar" style={{ background: 'var(--gold)' }}></div>
                                                    <div className="ev-proof-info">
                                                        <div className="ev-proof-nm">Gate {index + 1}</div>
                                                        <div className="ev-proof-tags"><span className="ev-proof-tag">In Progress</span></div>
                                                        <div style={{ fontSize: '8px', color: 'var(--gold)', marginTop: '3px' }}>Action Required</div>
                                                    </div>
                                                    <div className="ev-proof-status" style={{ background: 'var(--gold)' }}>Pending</div>
                                                </div>
                                            ) : (
                                                <div className="ev-proof" style={{ opacity: .4 }}>
                                                    <div className="ev-proof-bar" style={{ background: 'var(--border)' }}></div>
                                                    <div className="ev-proof-info">
                                                        <div className="ev-proof-nm">Gate {index + 1}</div>
                                                    </div>
                                                    <div className="ev-proof-status" style={{ background: 'var(--text-4)' }}>Locked</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="pright">
                    <div className="pcard">
                        <div className="pcard-h">
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="7" cy="7" r="6" /><path d="M7 4v3" /><circle cx="7" cy="9.5" r=".4" fill="currentColor" /></svg>
                            Alerts
                        </div>
                        <div className="al-item ur"><div className="al-dot" style={{ background: 'var(--rose)' }}></div>Sprint due soon</div>
                        <div className="al-item inf"><div className="al-dot" style={{ background: 'var(--sky)' }}></div>New guidance available</div>
                    </div>

                    <div className="pcard">
                        <div className="pcard-h">
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M7 1l.7 2.3L10 4l-2.3.7L7 7l-.7-2.3L4 4l2.3-.7z" /></svg>
                            Mentor
                        </div>
                        <div className="m-card">
                            <div className="m-av">MR</div>
                            <div>
                                <div className="m-nm">{podData?.podMembership?.instructorName || 'Instructor'}</div>
                                <div className="m-rl">Pod Guide</div>
                                <div className="m-st"><div style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--teal)' }}></div>Available</div>
                            </div>
                        </div>
                        <div className="m-quote">Ensure daily routines are followed and focus on friction points.</div>
                        <div className="m-hrs">
                            <div><div className="m-hrs-lbl">Office Hours</div><div className="m-hrs-val">Thu 4–5 PM</div></div>
                            <button className="m-ask" onClick={() => navigate('/innovation-pod/pod-room')}>Message</button>
                        </div>
                    </div>
                </div>
            </div>

            <IntendedOutcomesModal
                isOpen={isOutcomesModalOpen}
                onClose={() => setIsOutcomesModalOpen(false)}
                outcomes={podData?.intendedOutcomes || []}
            />

            <ProblemStatementModal
                isOpen={isProblemModalOpen}
                onClose={() => setIsProblemModalOpen(false)}
                statement={podData?.problemStatement || ''}
            />
        </PodLayout>
    );
}
